import { Box, VStack } from '@chakra-ui/react';
import { ReactElement, useState, useMemo } from 'react';
import { SegmentControl, SegmentItem } from './SegmentControl';

type Props = {
  segments: SegmentItem[];
  defaultActiveId?: string;
  onSegmentChange?: (segmentId: string) => void;
  showContent?: boolean;
  contentGap?: string;
  /**
   * If true, keeps all previously visited segments mounted in the DOM (preserves state).
   * If false, only the active segment is mounted (saves memory, loses state).
   * @default true
   */
  keepMounted?: boolean;
};

export const SegmentControlWithContent = ({
  segments,
  defaultActiveId,
  onSegmentChange,
  showContent = true,
  contentGap = '20px',
  keepMounted = true,
}: Props): ReactElement => {
  const [activeSegmentId, setActiveSegmentId] = useState<string>(
    defaultActiveId || segments[0]?.id || ''
  );
  const [visitedSegmentIds, setVisitedSegmentIds] = useState<Set<string>>(() => {
    const initialId = defaultActiveId || segments[0]?.id;
    return initialId ? new Set([initialId]) : new Set();
  });

  const handleSegmentChange = (segmentId: string): void => {
    setActiveSegmentId(segmentId);
    if (keepMounted) {
      setVisitedSegmentIds((prev) => new Set([...prev, segmentId]));
    }
    onSegmentChange?.(segmentId);
  };

  const segmentsToRender = useMemo(() => {
    if (keepMounted) {
      // Render all visited segments (preserves state)
      return segments.filter((segment) => visitedSegmentIds.has(segment.id));
    }
    // Only render active segment (saves memory, loses state)
    return segments.filter((segment) => segment.id === activeSegmentId);
  }, [keepMounted, segments, visitedSegmentIds, activeSegmentId]);

  return (
    <VStack gap={contentGap} align="stretch">
      <SegmentControl
        segments={segments}
        activeSegmentId={activeSegmentId}
        onSegmentChange={handleSegmentChange}
      />
      {showContent && (
        <Box mt={contentGap} pt={contentGap} borderTop="default" borderColor="border.accent">
          {segmentsToRender.map((segment) => {
            const isActive = segment.id === activeSegmentId;
            return (
              <Box key={segment.id} display={isActive ? 'block' : 'none'} aria-hidden={!isActive}>
                {segment.content}
              </Box>
            );
          })}
        </Box>
      )}
    </VStack>
  );
};
